import React, {useState, useEffect} from 'react'
import PhoneInput, {
  isValidPhoneNumber,
  getCountryCallingCode,
  formatPhoneNumber,
} from 'react-phone-number-input'
import styled from 'styled-components'
import 'react-phone-number-input/style.css'
import {Dispatch, SetStateAction} from 'react'
import type {CountryCode, E164Number} from 'libphonenumber-js'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { getCountries } from 'react-phone-number-input'

const countries = getCountries().map((code) => ({
  code,
  callingCode: getCountryCallingCode(code),
  flagUrl: `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`,
}))

interface CountrySelectProps {
  value?: CountryCode
  onChange: (country: CountryCode) => void
  locale?: string
}

const CustomCountrySelect = ({ value, onChange, locale }: CountrySelectProps) => {
  const [open, setOpen] = useState(false)

  const selected = countries.find(c => c.code === value)

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 p-2 bg-[#4646553a] rounded-md ${
          locale === 'ar' ? 'ml-2' : 'mr-2'
        }`}
      >
        {selected && (
          <>
            <img src={selected.flagUrl} className="w-5 h-4 rounded-sm" />
            <span className="text-xs font-medium">
              +{selected.callingCode}
            </span>
          </>
        )}
        <span className="PhoneInputCountrySelectArrow" />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={`absolute z-50 mt-1 w-56 bg-white rounded-md shadow-lg border
          ${locale === 'ar' ? 'right-0' : 'left-0'}`}
        >
          <div className="max-h-60 overflow-y-auto">
            {countries.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  onChange(country.code)
                  setOpen(false)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
              >
                <img
                  src={country.flagUrl}
                  className="w-5 h-4 rounded-sm"
                />
                <span className="text-sm">
                  {country.code} +{country.callingCode}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface StyledPhoneInputProps {
  isError?: boolean
}

const StyledPhoneInput = styled(PhoneInput)<StyledPhoneInputProps & { $locale?: string }>`
  &.PhoneInput {
    display: flex;
    flex-direction: ${({ $locale }) =>
      $locale === 'ar' ? 'row-reverse' : 'row'};
    border: ${({ isError }) =>
      isError ? '1px solid #ef4444' : '1px solid #d1d5db'};
    border-radius: 0.375rem;
    transition: all 0.2s;

    &:focus-within {
      border: 1px solid #000;
    }
  }

  .PhoneInputCountrySelectArrow {
    margin-left: 0.5rem;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 6px solid #000;
    border-bottom: 0;

    transform: none !important;
    rotate: 0deg !important;
    opacity: 1;
  }

  .PhoneInputInput {
    width: 100%;
    border: none;
    font-size: 0.75rem;
    direction: ${({ $locale }) =>
      $locale === 'ar' ? 'rtl' : 'ltr'};
    text-align: ${({ $locale }) =>
      $locale === 'ar' ? 'right' : 'left'};

    &:focus {
      outline: none;
      border: none;
      box-shadow: none;
    }

    &::placeholder {
      font-weight: 100;
      direction: ${({ $locale }) =>
        $locale === 'ar' ? 'rtl' : 'ltr'};
      text-align: ${({ $locale }) =>
        $locale === 'ar' ? 'right' : 'left'};
    }
  }
`

interface Props {
  error: string | undefined
  isError: boolean | string | undefined
  label: string | undefined
  values: any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  setFieldError: (field: string, message: string | undefined) => void
  setIso2: Dispatch<SetStateAction<string>>
  fieldPhoneName?: string
  fieldCountryCodeName?: string
  placeholder?: string
}

const TestPhoneInput = ({
  error,
  isError,
  label,
  values,
  setFieldValue,
  setFieldError,
  setIso2,
  fieldPhoneName = 'phone',
  fieldCountryCodeName = 'country_code',
  placeholder
}: Props) => {
  const isRequired = label?.includes('*')
  const labelText = isRequired ? label?.replace('*', '') : label
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("IL")

  // Format value for PhoneInput - remove leading zero for display in international format
  const formatValueForInput = () => {
    if (!values[fieldPhoneName]) return '';
    
    const phoneWithoutLeadingZero = values[fieldPhoneName].startsWith('0') 
      ? values[fieldPhoneName].substring(1)
      : values[fieldPhoneName];
      
    return `+${values[fieldCountryCodeName]}${phoneWithoutLeadingZero}`;
  }

  const {t} = useTranslation('removePhone');
  const {locale} = useRouter();

  const countrySelectComponent = React.useCallback(
    (props: any) => <CustomCountrySelect {...props} locale={locale} />,
    [locale]
  )

  return (
    <>
      <div>
        <label htmlFor={'phone'} className={`w-full ${locale === 'ar' ? 'text-right' : 'text-left'} block text-xs`}>
          {labelText}
          {isRequired && (
            <span className="text-red-600 text-lg font-bold leading-none">
              *
            </span>
          )}
        </label>
        <StyledPhoneInput
          $locale={locale}
          isError={isError}
          defaultCountry="IL"
          country={selectedCountry}
          countrySelectComponent={countrySelectComponent}
          placeholder={placeholder ? placeholder : `${t('placeholder')}`}
          value={formatValueForInput()}
          onChange={(newValue: string | undefined) => {
            if (!newValue) {
              setFieldValue(fieldPhoneName, '')
              setFieldValue(fieldCountryCodeName, '')
              return
            }

            const countryCode = getCountryCallingCode(selectedCountry)

            let phoneNumberOnly = newValue

            if (newValue.startsWith(`+${countryCode}`)) {
              phoneNumberOnly = newValue.substring(countryCode.length + 1)
            }

            if (phoneNumberOnly && !phoneNumberOnly.startsWith('0')) {
              phoneNumberOnly = '0' + phoneNumberOnly
            }

            setFieldValue(fieldCountryCodeName, countryCode)
            setFieldValue(fieldPhoneName, phoneNumberOnly)
            setIso2(selectedCountry)
          }}
          onCountryChange={(country: CountryCode | undefined) => {
            if (country) {
              setSelectedCountry(country)
              const countryCode = getCountryCallingCode(country)
              setFieldValue(fieldCountryCodeName, countryCode)
              setIso2(country)
            }
          }}
        />
        {isError && (
          <div className="mt-1 text-left text-sm text-red font-semibold">
            {error}
          </div>
        )}
      </div>
    </>
  )
}

export default TestPhoneInput
